import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Target} from '../../icons';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {ECOMMERCE_ROAS_METRIC_NAME} from '../../dashboard/metrics';
import {formatCompactMoney, formatGoalValue} from './panelFormatters';
import {
    economyLineFor,
    economyReasonText,
    formatEconomyDate,
    formatRoas,
    lineCurrencies,
    reasonFor,
} from './economyFormatters';
import './LineGoalEconomicsSuggestion.scss';

// Los tres «por qué» que el contrato nombra (§3.4, copy §4). Un `why` del backend que no sea uno de
// éstos se ignora y manda la cuenta del FE, que usa los mismos números.
const WHY_KEYS = new Set(['why_higher', 'why_lower', 'why_no_store']);

/**
 * El caso del «por qué» con los números de la sugerencia, para cuando el backend no lo manda:
 * - sin tienda en la línea, el objetivo se medirá contra ventas atribuidas (`why_no_store`);
 * - un objetivo actual de ROAS por encima del sugerido frena inversión rentable (`why_higher`);
 * - uno por debajo del equilibrio se cumple perdiendo plata (`why_lower`).
 * Exportado para testearlo sin montar nada.
 */
export function suggestionWhyKey(suggestion) {
    if (!suggestion) return null;
    if (suggestion.has_store === false) return 'why_no_store';

    const current = suggestion.current_goal || {};
    const value = Number(current.value);
    if (current.value === null || current.value === undefined || !Number.isFinite(value)) return null;
    if (current.metric_name && suggestion.metric_name && current.metric_name !== suggestion.metric_name) return null;
    if (value < Number(suggestion.breakeven_roas)) return 'why_lower';
    if (value > Number(suggestion.suggested_goal_roas)) return 'why_higher';
    return null;
}

function keyOf(part) {
    return part && part.key ? String(part.key).split('.').pop() : null;
}

/**
 * El «por qué» en dos partes: la frase base (margen de contribución → centavos por peso → ROAS de
 * equilibrio) y el caso. El backend los manda armados (`why: {base: {key, params}, case: {key,
 * params} | null}`, claves relativas a `client_panel.line_goal_modal.economics.`) y MANDAN: son los
 * mismos números que la card y el MCP. Sin `why` (backend viejo) se arman acá con el margen del
 * bloque `economy` (`cmFallback`) y `suggestionWhyKey`. Devuelve `{base, caseKey, caseParams}`.
 */
export function resolveWhy(suggestion, cmFallback = null) {
    const why = suggestion && suggestion.why;
    if (why && typeof why === 'object' && (why.base || why.case !== undefined)) {
        const caseKey = keyOf(why.case);
        return {
            base: keyOf(why.base) === 'why' ? (why.base.params || {}) : null,
            caseKey: WHY_KEYS.has(caseKey) ? caseKey : null,
            caseParams: (why.case && why.case.params) || {},
        };
    }
    const cm = Number(cmFallback);
    const current = (suggestion && suggestion.current_goal) || {};
    return {
        base: Number.isFinite(cm) && cm > 0 && cmFallback !== null
            ? {cm, cents: Math.round(cm), breakeven: suggestion && suggestion.breakeven_roas} : null,
        caseKey: suggestionWhyKey(suggestion),
        caseParams: {current: current.value},
    };
}

/**
 * Bloque «Sugerido desde la economía del negocio» del modal de objetivo (mockup 4).
 *
 * Pide su propio dato (`GET business-unit/<id>/economics/suggestion/`): el modal no es una card del
 * Panel y el endpoint existe para esto. Nunca bloquea el modal: mientras carga o si falla no dibuja
 * nada y el objetivo se define igual, que es lo que el usuario vino a hacer.
 *
 * «Usar sugerido» NO guarda: completa el formulario (`onUse`) y el usuario guarda con el botón de
 * siempre. Nada se aplica solo.
 */
export function LineGoalEconomicsSuggestion({lineId, economy, clientId, onUse, disabled = false,
                                                hidden = false, loadedMetric = null, currentGoal = null}) {
    const {t, i18n} = useTranslation();
    const enabled = !!economy && economy.enabled === true;
    const [state, setState] = useState({loading: enabled, data: null});

    useEffect(() => {
        if (!enabled || lineId === null || lineId === undefined) {
            setState({loading: false, data: null});
            return undefined;
        }
        let alive = true;
        setState({loading: true, data: null});
        ClientEconomicsApi.getSuggestion(lineId).then((response) => {
            if (!alive) return;
            setState({loading: false, data: response && response.status === 'OK' ? (response.data || null) : null});
        }).catch(() => {
            if (alive) setState({loading: false, data: null});
        });
        return () => { alive = false; };
    }, [enabled, lineId]);

    // `hidden`: el modal lo monta desde que abre (así la sugerencia se pide EN PARALELO con el objetivo
    // y no después) y lo muestra recién cuando el formulario está listo.
    if (hidden || !enabled || state.loading || !state.data) return null;
    const suggestion = state.data;
    const lineEconomy = economyLineFor(economy, lineId);

    if (suggestion.available !== true) {
        // Sin perfil, una línea que dice qué se gana cargándolo y adónde ir. Sin módulo, nada.
        if (suggestion.reason === 'MODULE_DISABLED') return null;
        if (suggestion.reason === 'NOT_CONFIGURED') {
            if (!clientId) return null;
            return (
                <p className="eco-sug-empty" data-testid="eco-sug-not-configured">
                    <Link to={`/client/${clientId}/economia`}>
                        {t('client_panel.line_goal_modal.economics.not_configured')}
                    </Link>
                </p>
            );
        }
        // Con la economía cargada y sin sugerido por otro motivo (otra moneda, margen que no alcanza,
        // un dato que falta): se dice por qué, en vez de callarse (R-ecofe-19).
        const reasonText = economyReasonText(suggestion.reason, t,
            lineCurrencies(lineEconomy || suggestion, suggestion.currency || economy.currency));
        if (!reasonText) return null;
        return <p className="eco-sug-empty -reason" data-testid="eco-sug-reason">{reasonText}</p>;
    }

    // La sugerencia es de ROAS (Ecommerce): a una línea que ya mide OTRA métrica no se le ofrece
    // cambiarla desde acá sin explicar por qué (R-ecofe-01; si se quiere, lo decide fede).
    const suggestionMetric = suggestion.metric_name || ECOMMERCE_ROAS_METRIC_NAME;
    if (loadedMetric && loadedMetric !== suggestionMetric) return null;

    const currency = suggestion.currency || (lineEconomy && lineEconomy.currency) || economy.currency || null;
    const suggested = suggestion.suggested_goal_roas;
    const profile = suggestion.profile || {};
    const capReason = suggestion.spend_cap === null || suggestion.spend_cap === undefined
        ? economyReasonText(reasonFor(suggestion, 'spend_cap'), t) : null;
    // El formulario ya tiene EXACTAMENTE el sugerido: «Usar sugerido» no cambiaría nada y guardar
    // dejaría un «objetivo aplicado desde la economía» espurio en la Bitácora.
    const alreadySuggested = !!currentGoal && currentGoal.metricName === suggestionMetric
        && Number(currentGoal.value) === Number(suggested);
    // Sin `why` del backend, el margen de contribución sale del MISMO bloque que la card (el de la
    // línea: puede tener override; el del cliente si la línea no vino).
    const derived = (lineEconomy && lineEconomy.derived) || economy.client_totals || null;
    const why = resolveWhy(suggestion, derived ? derived.contribution_margin_pct : null);

    return (
        <section className="eco-sug" data-testid="eco-sug" aria-labelledby={`eco-sug-title-${lineId}`}>
            <h3 className="eco-sug__head" id={`eco-sug-title-${lineId}`}>
                <Target size={16} weight="bold"/>
                {t('client_panel.line_goal_modal.economics.title')}
            </h3>
            <div className="eco-sug__grid">
                <div className="eco-sug__item">
                    <span className="eco-sug__label">{t('client_panel.line_goal_modal.economics.breakeven')}</span>
                    <span className="eco-sug__value">{formatRoas(suggestion.breakeven_roas) || '—'}</span>
                </div>
                <div className="eco-sug__item -main">
                    <span className="eco-sug__label">{t('client_panel.line_goal_modal.economics.suggested')}</span>
                    <span className="eco-sug__value">{suggested !== null && suggested !== undefined
                        ? `≥ ${formatRoas(suggested)}` : '—'}</span>
                </div>
                <div className="eco-sug__item">
                    <span className="eco-sug__label">{t('client_panel.line_goal_modal.economics.cap')}</span>
                    <span className="eco-sug__value">{formatCompactMoney(currency, suggestion.spend_cap) || '—'}</span>
                    {capReason && <span className="eco-sug__reason" data-testid="eco-sug-cap-reason">{capReason}</span>}
                </div>
            </div>
            <p className="eco-sug__why" data-testid="eco-sug-why">
                {why.base && (
                    <>
                        {t('client_panel.line_goal_modal.economics.why', {
                            cm: formatGoalValue(why.base.cm),
                            cents: why.base.cents,
                            breakeven: formatGoalValue(why.base.breakeven),
                        })}
                        {' '}
                    </>
                )}
                {why.caseKey && (
                    <>
                        {t(`client_panel.line_goal_modal.economics.${why.caseKey}`, {
                            current: formatGoalValue(why.caseParams.current),
                        })}
                        {' '}
                    </>
                )}
                <span className="eco-sug__source">
                    {t('client_panel.line_goal_modal.economics.source', {
                        version: profile.version || '—',
                        date: formatEconomyDate(profile.effective_from, i18n && i18n.language) || '—',
                        interpolation: {escapeValue: false},
                    })}
                </span>
            </p>
            {suggested !== null && suggested !== undefined && (
                <div className="eco-sug__acts">
                    <button type="button" className="btn -alt eco-sug__use" disabled={disabled || alreadySuggested}
                            data-testid="eco-sug-use"
                            onClick={() => onUse({metricName: suggestionMetric, value: suggested})}>
                        {t('client_panel.line_goal_modal.economics.use', {roas: formatGoalValue(suggested)})}
                    </button>
                </div>
            )}
        </section>
    );
}

export default LineGoalEconomicsSuggestion;
